import React, { memo, useEffect } from "react";
import { Platform, StyleSheet } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import styled, { useTheme } from "styled-components/native";
import { ButtonContent } from "./ButtonContent";

const isWeb = Platform.OS === "web";
const BUTTON_HEIGHT = 48;
const BUTTON_WIDTH = isWeb ? 181 : 167;

type AISearchButtonVisualProps = {
  typography: any;
  height?: number;
};

export const AISearchButtonVisual = memo(
  ({ typography, height = BUTTON_HEIGHT }: AISearchButtonVisualProps) => {
    const rotation = useSharedValue(0);
    const shadowProgress = useSharedValue(0);
    const gradientTheme = useTheme();
    const COLOR_1 = gradientTheme["diam-01"] || "#804195";
    const COLOR_2 = gradientTheme["jewelry-01"] || "#58B8A5";
    const SHADOW_COLOR_1 = gradientTheme["diam-01"] || "#804195";
    const SHADOW_COLOR_2 = gradientTheme["jewelry-01"] || "#58B8A5";
    const SHADOW_COLOR_3 = "#6C7AC8";

    const GRADIENT_COLORS: readonly [string, string, string, string] = [
      COLOR_1,
      COLOR_2,
      COLOR_1,
      COLOR_2,
    ];
    const GRADIENT_START = { x: 0, y: 0 };
    const GRADIENT_END = { x: 1, y: 1 };

    const webGradientStyle = isWeb
      ? { animation: "rotateGradient 2.5s linear infinite" }
      : undefined;

    if (isWeb && typeof document !== "undefined") {
      const styleId = "ai-search-button-animations";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
        @keyframes rotateGradient {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes rotateShadow {
          0% { box-shadow: 0 0 20px 4px ${SHADOW_COLOR_1}; }
          33% { box-shadow: 0 0 25px 6px ${SHADOW_COLOR_2}; }
          66% { box-shadow: 0 0 22px 5px ${SHADOW_COLOR_3}; }
          100% { box-shadow: 0 0 20px 4px ${SHADOW_COLOR_1}; }
        }
        .ai-search-glow-container {
          animation: rotateShadow 3s ease-in-out infinite;
        }
      `;
        document.head.appendChild(style);
      }
    }

    useEffect(() => {
      if (!isWeb) {
        // Border rotation animation
        rotation.value = withRepeat(
          withTiming(360, {
            duration: 2500,
            easing: Easing.linear,
          }),
          -1,
          false
        );

        // Shadow color cycling animation (0 -> 1 -> 2 -> 0)
        shadowProgress.value = withRepeat(
          withTiming(3, {
            duration: 3000,
            easing: Easing.inOut(Easing.ease),
          }),
          -1,
          false
        );
      }
    }, [rotation, shadowProgress]);

    const animatedGradientStyle = useAnimatedStyle(() => {
      "worklet";
      return {
        transform: [{ rotate: `${rotation.value}deg` }],
      };
    }, [rotation]);

    // Animated shadow style for native
    const animatedShadowStyle = useAnimatedStyle(() => {
      "worklet";
      // Interpolate shadow radius and opacity based on progress
      const progress = shadowProgress.value % 3;
      const pulseIntensity = Math.sin((progress / 3) * Math.PI * 2) * 0.3 + 0.7;

      return {
        shadowOpacity: 0.8 + pulseIntensity * 0.2,
        shadowRadius: 14 + pulseIntensity * 8,
      };
    }, [shadowProgress]);

    // For web, we use CSS class for shadow animation
    const webGlowClass = isWeb ? "ai-search-glow-container" : undefined;

    return (
      <Animated.View
        style={[
          styles.glowContainer,
          !isWeb && animatedShadowStyle,
          { width: BUTTON_WIDTH, height: height },
        ]}
        // @ts-ignore - className for web
        className={webGlowClass}
      >
        <GradientWrapper width={BUTTON_WIDTH} height={height}>
          {isWeb ? (
            <LinearGradient
              colors={GRADIENT_COLORS}
              start={GRADIENT_START}
              end={GRADIENT_END}
              style={[styles.gradientBorder, webGradientStyle as any]}
            />
          ) : (
            <Animated.View
              style={[styles.gradientBorder, animatedGradientStyle]}
            >
              <LinearGradient
                colors={GRADIENT_COLORS}
                start={GRADIENT_START}
                end={GRADIENT_END}
                style={StyleSheet.absoluteFill}
              />
            </Animated.View>
          )}
        </GradientWrapper>

        <ButtonContent typography={typography} />
      </Animated.View>
    );
  }
);

const styles = StyleSheet.create({
  glowContainer: {
    borderRadius: BUTTON_HEIGHT / 2,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#6C7AC8",
    shadowOffset: { width: 3, height: 3 },
    shadowOpacity: 1,
    shadowRadius: 14,
    elevation: 10,
  },
  gradientBorder: {
    width: BUTTON_WIDTH * 2.5,
    height: BUTTON_WIDTH * 2.5,
    borderRadius: BUTTON_WIDTH * 1.25,
  },
});

const GradientWrapper = styled.View<{
  width: number;
  height: number;
}>`
  position: absolute;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border-radius: ${({ height }) => height / 2}px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
`;
